// src/screen/Finance/FinanceDashboard.tsx
import apiClient from "../../services/apiClient";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ScreenLockerWeb from "../../components/ScreenLockerWeb";
import { useDragScroll } from "../../hooks/useDragScroll";
import {
  PieChart as RPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart as RBarChart,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  LabelList,
  CartesianGrid,
  Legend,
} from "recharts";
import CategoriesModal from "../../components/Modal/CategoriesModal";
import AddExpenseModal from "../../components/Modal/AddExpenseModal";
import EditExpenseModal from "../../components/Modal/EditExpenseModal";
import { getEventInventory } from "../../services/eventInventoryApi";
import {
  fetchInventoryPools,
  getInventoryByPool,
} from "../../services/inventoryPools";
import { formatMoney } from "../../services/currency";

/* ----------------------- Types ----------------------- */
interface EventLite {
  eventId: string;
  title: string;
  startDateTime: string;
  eventStartDateTime?: string;
  endDateTime?: string;
  eventEndDateTime?: string;
  endDate?: string;
  totalRevenue?: number;
  totalNetProfit?: number;
  bestSellerItem?: { itemName: string; quantitySold: number } | null;
}
interface BestSellerRow {
  itemName: string;
  quantitySold: number;
  totalRevenue: number;
  inventoryItemId?: string;
  category?: string;
  sku?: string;
  imageUrl?: string;
}
interface InventoryLite {
  _id: string;
  itemName?: string;
  sku?: string;
  quantity?: number;
  imageUrl?: string;
}
interface BestSellerInventorySummary {
  poolQuantity: number;
  assignedQuantity: number;
}
interface PaymentBreakdownItem {
  paymentMethod: string;
  totalAmount: number;
}
interface RangeFinance {
  totalRevenue: number;
  totalRefunds: number;
  totalDiscounts: number;
  totalCost: number;
  totalNetProfit: number;
  totalCommissionPaid?: number;
  bestSellers: BestSellerRow[];
  events: EventLite[];
  businessExpenses?: BusinessExpense[];
  paymentMethodBreakdown?: PaymentBreakdownItem[];
  paymentMethodBreakdownAll?: PaymentBreakdownItem[];
}

type BusinessExpense = {
  _id: string;
  description: string;
  cost: number;
  createdAt: string;
  date?: string;
  expenseDate?: string;
  category?: string; // ← NEW: show & filter by this
};

type ExpenseCategory = { _id: string; name: string };

/* ----------------------- Date helpers ----------------------- */
const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const addDays = (d: Date, n: number) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
const startOfWeek = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay());
const toYMD_UTC = (d: Date) => {
  const z = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  return z.toISOString().slice(0, 10);
};
const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const formatEventDate = (value?: string) => {
  if (!value || typeof value !== "string") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

type DateRange = { start: Date; end: Date };
type PresetKey =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "last90"
  | "thisMonth"
  | "lastMonth"
  | "lastYear"
  | "ytd"
  | "allTime"
  | "custom";

const makePresetRange = (k: PresetKey, allTimeStart?: Date | null): DateRange => {
  const now = new Date();
  switch (k) {
    case "today":
      return { start: startOfDay(now), end: endOfDay(now) };
    case "yesterday":
      return {
        start: startOfDay(addDays(now, -1)),
        end: endOfDay(addDays(now, -1)),
      };
    case "last7":
      return { start: startOfDay(addDays(now, -6)), end: endOfDay(now) };
    case "last30":
      return { start: startOfDay(addDays(now, -29)), end: endOfDay(now) };
    case "last90":
      return { start: startOfDay(addDays(now, -89)), end: endOfDay(now) };
    case "thisMonth":
      return { start: startOfMonth(now), end: endOfDay(now) };
    case "lastMonth": {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { start: s, end: endOfMonth(s) };
    }
    case "lastYear": {
      const s = new Date(now.getFullYear() - 1, 0, 1);
      const e = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
      return { start: s, end: e };
    }
    case "ytd":
      return { start: new Date(now.getFullYear(), 0, 1), end: endOfDay(now) };
    case "allTime":
      return {
        start: startOfDay(allTimeStart || new Date(2000, 0, 1)),
        end: endOfDay(now),
      };
    default:
      return { start: startOfMonth(now), end: endOfDay(now) };
  }
};

const fmtRangeLabel = (r: DateRange) => {
  const sameYear = r.start.getFullYear() === r.end.getFullYear();
  const sameMonth = sameYear && r.start.getMonth() === r.end.getMonth();
  const mon = (d: Date) => d.toLocaleDateString("en-US", { month: "short" });
  const day = (d: Date) => d.getDate();
  const yr = (d: Date) => d.getFullYear();
  if (sameYear && sameMonth)
    return `${mon(r.start)} ${day(r.start)} – ${day(r.end)}, ${yr(r.end)}`;
  if (sameYear)
    return `${mon(r.start)} ${day(r.start)} – ${mon(r.end)} ${day(r.end)}, ${yr(
      r.end
    )}`;
  return `${mon(r.start)} ${day(r.start)}, ${yr(r.start)} – ${mon(r.end)} ${day(
    r.end
  )}, ${yr(r.end)}`;
};

/* ----------------------- API (web) ----------------------- */
async function getFinanceByRangeAutoWeb(
  startYMD: string,
  endYMD: string
): Promise<RangeFinance> {
  try {
    const { data } = await apiClient.post("/finance/range", {
      startDate: startYMD,
      endDate: endYMD,
    });
    return data;
  } catch {
    const { data } = await apiClient.get(
      `/finance/range?startDate=${encodeURIComponent(
        startYMD
      )}&endDate=${encodeURIComponent(endYMD)}`
    );
    return data;
  }
}

async function getBusinessExpenses(year: string) {
  const { data } = await apiClient.get(`/finance/business-expenses/${year}`);
  return data as BusinessExpense[];
}

async function addBusinessExpense(expense: {
  description: string;
  cost: number;
  date: string;
  category?: string; // NEW
}) {
  const { data } = await apiClient.put(`/finance/business-expense`, {
    description: expense.description,
    cost: expense.cost,
    category: expense.category,
    createdAt: expense.date,
  });
  return data;
}

const getExpenseDateValue = (expense: BusinessExpense) =>
  expense.date || expense.expenseDate || expense.createdAt;

const getExpenseDate = (expense: BusinessExpense) => {
  const raw = getExpenseDateValue(expense);
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

async function deleteBusinessExpense(year: string, id: string) {
  await apiClient.delete(`/finance/business-expense/${year}/${id}`);
}

async function fetchEventsLite(): Promise<EventLite[]> {
  const { data } = await apiClient.get(`/events`);
  return Array.isArray(data) ? (data as EventLite[]) : [];
}

async function fetchExpenseCategories(): Promise<ExpenseCategory[]> {
  const { data } = await apiClient.get(`/finance/expense-categories`);
  return Array.isArray(data) ? data : [];
}

async function fetchInventoryLite(): Promise<InventoryLite[]> {
  const { data } = await apiClient.get("/inventory");
  const items = Array.isArray((data as any)?.items) ? (data as any).items : [];
  return items as InventoryLite[];
}

/* -------------------- UI helpers -------------------- */
const currency = (n: number) => formatMoney(n);
const summaryCurrency = (label: string, value?: number) =>
  label === "Net Profit" && Number(value || 0) < 0
    ? currency(Math.abs(Number(value || 0)))
    : currency(Number(value || 0));
const normalizeCategoryLabel = (value?: string) => {
  const label = (value || "").trim();
  return label || "Uncategorized";
};
const formatCount = (n: number) => Number(n || 0).toLocaleString();
const formatPercent = (n: number) => `${Number(n || 0).toFixed(1)}%`;
const formatCategoryMetric = (value: number, name: string) => {
  if (name === "Revenue %") return [formatPercent(Number(value)), name];
  return [formatCount(Number(value)), name];
};
const makeInventoryKey = (itemName?: string, sku?: string) => {
  const skuKey = (sku || "").trim().toLowerCase();
  if (skuKey) return `sku:${skuKey}`;
  return `name:${(itemName || "").trim().toLowerCase()}`;
};
const normalizeInventoryText = (value?: string) =>
  String(value || "")
    .trim()
    .toLowerCase();
const readInventoryId = (value: any) =>
  String(
    value?.inventoryItemId?._id ||
      value?.inventoryItem?._id ||
      value?.inventory?._id ||
      value?.inventoryId ||
      value?._id ||
      ""
  ).trim();
const matchesInventoryRecord = (
  value: any,
  bestSeller: { itemName?: string; sku?: string },
  inventoryId?: string
) => {
  const candidateId = readInventoryId(value);
  if (inventoryId) return Boolean(candidateId && candidateId === inventoryId);

  const candidateSku = normalizeInventoryText(
    value?.inventoryItemId?.sku ||
      value?.inventoryItem?.sku ||
      value?.inventory?.sku ||
      value?.sku
  );
  const bestSku = normalizeInventoryText(bestSeller.sku);
  if (bestSku) return Boolean(candidateSku && bestSku === candidateSku);

  const candidateName = normalizeInventoryText(
    value?.inventoryItemId?.itemName ||
      value?.inventoryItem?.itemName ||
      value?.inventory?.itemName ||
      value?.itemName
  );
  const bestName = normalizeInventoryText(bestSeller.itemName);
  return Boolean(bestName && candidateName && bestName === candidateName);
};
const readAssignedQuantity = (value: any) => {
  const directCandidates = [
    value?.remainingQuantity,
    value?.quantityRemaining,
    value?.availableQuantity,
    value?.quantityLeft,
  ];
  for (const candidate of directCandidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return Math.max(0, parsed);
  }

  const allocated = Number(value?.allocatedQuantity ?? value?.allocatedQty ?? 0);
  const sold = Number(value?.soldQuantity ?? value?.quantitySold ?? 0);
  if (Number.isFinite(allocated) || Number.isFinite(sold)) {
    return Math.max(0, allocated - sold);
  }
  return 0;
};
const isOpenEventStatus = (status?: string) =>
  String(status || "")
    .trim()
    .toLowerCase() !== "completed";
const truncate = (s: string, n = 40) =>
  s?.length > n ? s.slice(0, n - 1) + "…" : s;

/** Softer accessible palette */
const PALETTE = {
  ink: "#0f172a",
  muted: "#64748b",
  border: "#e2e8f0",
  card: "#ffffff",
  green: "#16a34a",
  blue: "#2563eb",
  red: "#dc2626",
  amber: "#d97706",
  violet: "#7c3aed",
  slate: "#475569",
};

const FinanceSkeletonBlock = ({
  className,
}: {
  className: string;
}) => (
  <div
    className={`animate-pulse rounded-md border border-slate-200 bg-white ${className}`}
  >
    <div className="h-full w-full rounded-md bg-slate-100/90" />
  </div>
);

/* -------------------- Date Range Controls -------------------- */
function DateRangeControls({
  preset,
  range,
  onPreset,
  onApply,
}: {
  preset: PresetKey;
  range: DateRange;
  onPreset: (k: PresetKey) => void;
  onApply: (r: DateRange) => void;
}) {
  const [tempStart, setTempStart] = useState<Date | null>(range.start);
  const [tempEnd, setTempEnd] = useState<Date | null>(range.end);
  const [month, setMonth] = useState<Date>(
    new Date(range.start.getFullYear(), range.start.getMonth(), 1)
  );
  const [isOpen, setIsOpen] = useState(false);
  const [activeField, setActiveField] = useState<"start" | "end" | null>(null);
  const formatInputDate = (d: Date | null) =>
    d
      ? d.toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
        })
      : "";
  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 21 }, (_, i) => y - 10 + i);
  }, []);
  const monthNames = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );

  useEffect(() => {
    setTempStart(range.start);
    setTempEnd(range.end);
    setMonth(new Date(range.start.getFullYear(), range.start.getMonth(), 1));
  }, [range.start, range.end]);

  const onPickDay = (d: Date) => {
    if (!tempStart || activeField === "start" || (tempStart && tempEnd)) {
      setTempStart(d);
      setTempEnd(null);
      setActiveField("end");
      return;
    }
    if (d < tempStart) {
      setTempEnd(tempStart);
      setTempStart(d);
      onApply({ start: startOfDay(d), end: endOfDay(tempStart) });
      setIsOpen(false);
      setActiveField(null);
      return;
    }
    setTempEnd(d);
    onApply({ start: startOfDay(tempStart), end: endOfDay(d) });
    setIsOpen(false);
    setActiveField(null);
  };

  const days = (() => {
    const first = startOfMonth(month);
    const gridStart = startOfWeek(first);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(
        gridStart.getFullYear(),
        gridStart.getMonth(),
        gridStart.getDate() + i
      );
      return { date: d, inMonth: d.getMonth() === month.getMonth() };
    });
  })();

  const btn = (k: PresetKey, label: string) => (
    <button
      key={k}
      onClick={() => onPreset(k)}
      className={`px-2.5 py-1 rounded-md border text-xs transition bg-white
        ${
          preset === k
            ? "bg-blue-600 text-black border-blue-600"
            : "border-slate-300 hover:bg-slate-50"
        }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-2 ">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setIsOpen(true);
            setActiveField("start");
          }}
          className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md border border-slate-300 bg-white text-xs min-w-[120px]"
        >
          <span className="text-slate-800">
            {tempStart ? formatInputDate(tempStart) : "Start date"}
          </span>
          <span className="text-slate-500">📅</span>
        </button>
        <span className="text-slate-500">→</span>
        <button
          type="button"
          onClick={() => {
            setIsOpen(true);
            setActiveField("end");
          }}
          className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md border border-slate-300 bg-white text-xs min-w-[120px]"
        >
          <span className="text-slate-800">
            {tempEnd ? formatInputDate(tempEnd) : "End date"}
          </span>
          <span className="text-slate-500">📅</span>
        </button>
      </div>
      {btn("today", "Today")}
      {btn("yesterday", "Yesterday")}
      {btn("last7", "Last 7")}
      {btn("last30", "Last 30")}
      {btn("last90", "Last 90")}
      {btn("thisMonth", "This month")}
      {btn("lastMonth", "Last month")}
      {btn("lastYear", "Last year")}
      {btn("ytd", "YTD")}
      {btn("allTime", "All time")}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <button
            type="button"
            aria-label="Close calendar"
            className="absolute inset-0 bg-black/30"
            onClick={() => {
              setIsOpen(false);
              setActiveField(null);
            }}
          />
          <div className="relative border border-slate-300 rounded-md bg-white p-3 shadow-lg">
            <div className="flex items-center justify-between mb-1 gap-2">
              <button
                onClick={() =>
                  setMonth(
                    new Date(month.getFullYear(), month.getMonth() - 1, 1)
                  )
                }
                className="text-xs text-slate-600 hover:text-slate-900"
              >
                Prev
              </button>
              <div className="flex items-center gap-2">
                <select
                  value={month.getMonth()}
                  onChange={(e) =>
                    setMonth(
                      new Date(
                        month.getFullYear(),
                        Number(e.target.value),
                        1
                      )
                    )
                  }
                  className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                  aria-label="Select month"
                >
                  {monthNames.map((label, idx) => (
                    <option key={label} value={idx}>
                      {label}
                    </option>
                  ))}
                </select>
                <select
                  value={month.getFullYear()}
                  onChange={(e) =>
                    setMonth(
                      new Date(
                        Number(e.target.value),
                        month.getMonth(),
                        1
                      )
                    )
                  }
                  className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                  aria-label="Select year"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() =>
                  setMonth(
                    new Date(month.getFullYear(), month.getMonth() + 1, 1)
                  )
                }
                className="text-xs text-slate-600 hover:text-slate-900"
              >
                Next
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-[11px] text-slate-500 mb-1">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                <div key={d} className="text-center">
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {days.map(({ date, inMonth }) => {
                const isStart = !!tempStart && isSameDay(date, tempStart);
                const isEnd = !!tempEnd && isSameDay(date, tempEnd);
                const inRange =
                  !!tempStart &&
                  !!tempEnd &&
                  date >= tempStart &&
                  date <= tempEnd;
                const cls = [
                  "text-xs h-7 w-7 rounded-md flex items-center justify-center",
                  inMonth ? "text-slate-800" : "text-slate-400",
                  inRange ? "bg-blue-100" : "hover:bg-slate-100",
                  isStart || isEnd
                    ? "bg-blue-600 text-white hover:bg-blue-600"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <button
                    key={date.toISOString()}
                    onClick={() => onPickDay(date)}
                    className={cls}
                    aria-pressed={isStart || isEnd}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const getEventId = (ev: any) => ev?.eventId || ev?.id || ev?._id || "";
const getEventStartLikeDate = (ev: EventLite): Date | null => {
  const raw =
    ev?.startDateTime || ev?.eventStartDateTime || ev?.endDateTime || ev?.eventEndDateTime;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};
const getEarliestDate = (events: EventLite[]) => {
  let min: Date | null = null;
  for (const ev of events || []) {
    const d = getEventStartLikeDate(ev);
    if (!d) continue;
    if (!min || d.getTime() < min.getTime()) min = d;
  }
  return min;
};

/* -------------------- Main Component -------------------- */
const FinanceDashboard: React.FC = () => {
  type BestSortKey = "itemName" | "category" | "quantitySold" | "totalRevenue";
  type BestSortDir = "asc" | "desc";
  const navigate = useNavigate();

  const [preset, setPreset] = useState<PresetKey>("last30");
  const [range, setRange] = useState<DateRange>(() =>
    makePresetRange("last30")
  );
  const [loading, setLoading] = useState(false);
  const [supplementalLoading, setSupplementalLoading] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [data, setData] = useState<RangeFinance | null>(null);
  const [events, setEvents] = useState<EventLite[]>([]);
  const [expenses, setExpenses] = useState<BusinessExpense[]>([]);

  const [showAllCards, setShowAllCards] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [showAllAnalysis, setShowAllAnalysis] = useState(false);
  const [showAllBest, setShowAllBest] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);

  const [editingExpense, setEditingExpense] = useState<BusinessExpense | null>(
    null
  );
  const [showEditExpense, setShowEditExpense] = useState(false);
  const [selectedBestSeller, setSelectedBestSeller] =
    useState<BestSellerRow | null>(null);
  const [inventoryByKey, setInventoryByKey] = useState<
    Map<string, InventoryLite>
  >(new Map());
  const [inventoryById, setInventoryById] = useState<Map<string, InventoryLite>>(
    new Map()
  );
  const [bestSellerInventorySummary, setBestSellerInventorySummary] =
    useState<BestSellerInventorySummary>({
      poolQuantity: 0,
      assignedQuantity: 0,
    });
  const [bestSellerInventorySummaryCache, setBestSellerInventorySummaryCache] =
    useState<Map<string, BestSellerInventorySummary>>(new Map());
  const [bestSellerInventorySummaryLoading, setBestSellerInventorySummaryLoading] =
    useState(false);

  // NEW: show more/less for Business Expenses
  const [showAllExpenses, setShowAllExpenses] = useState(false);

  // Categories modal
  const [showCategories, setShowCategories] = useState(false);

  // NEW: expense category filtering state
  const [, setExpenseCategories] = useState<ExpenseCategory[]>([]);
  const [selectedExpenseCategory, setSelectedExpenseCategory] = useState<
    string | "All"
  >("All");

  // NEW: Best sellers filters
  const [bestCategoryFilter, setBestCategoryFilter] = useState<string>("All");
  const [bestSearch, setBestSearch] = useState<string>("");
  const [bestSort, setBestSort] = useState<{
    key: BestSortKey;
    dir: BestSortDir;
  }>({ key: "totalRevenue", dir: "desc" });
  const [eventSearch, setEventSearch] = useState<string>("");
  const [allTimeStart, setAllTimeStart] = useState<Date | null>(null);
  const [hasLoadedInventory, setHasLoadedInventory] = useState(false);
  const loadRequestRef = useRef(0);

  const startStr = toYMD_UTC(range.start);
  const endStr = toYMD_UTC(range.end);
  const year = String(range.start.getFullYear());

  const filteredEvents = useMemo(() => {
    const query = eventSearch.trim().toLowerCase();
    if (!query) return events;
    return events.filter((ev) =>
      String(ev.title || "")
        .toLowerCase()
        .includes(query)
    );
  }, [eventSearch, events]);

  const visibleEvents = useMemo(() => {
    if (showAllEvents) return filteredEvents;
    return [...filteredEvents].slice(0, 6);
  }, [filteredEvents, showAllEvents]);

  const loadSupplemental = useCallback(
    async (rangeFinance: RangeFinance, requestId: number) => {
      setSupplementalLoading(true);
      try {
        const fetchExpenses = async () => {
          const startYear = range.start.getFullYear();
          const endYear = range.end.getFullYear();
          const years = Array.from(
            { length: Math.max(1, endYear - startYear + 1) },
            (_, i) => String(startYear + i)
          );
          const byYear = await Promise.all(
            years.map((y) => getBusinessExpenses(y).catch(() => []))
          );
          return byYear.flat();
        };

        const [evts, exps, cats] = await Promise.all([
          fetchEventsLite().catch(() => []),
          fetchExpenses(),
          fetchExpenseCategories().catch(() => []),
        ]);

        if (loadRequestRef.current !== requestId) return;

        const fullEventsById = new Map<string, EventLite>();
        (evts || []).forEach((ev) => {
          const key = getEventId(ev);
          if (key) fullEventsById.set(key, ev);
        });
        const mergedEvents = (rangeFinance.events || []).map((ev) => {
          const key = getEventId(ev);
          const full = key ? fullEventsById.get(key) : undefined;
          return {
            ...(full || {}),
            ...ev,
          };
        });
        const resolvedEvents = mergedEvents.length ? mergedEvents : evts;
        setEvents(resolvedEvents.length ? resolvedEvents : rangeFinance.events || []);
        setAllTimeStart(getEarliestDate(evts));
        setExpenses(exps);

        const fromAPI = cats || [];
        const fromExpenses = Array.from(
          new Set(
            (exps || [])
              .map((e) => (e.category || "").trim())
              .filter((c) => c.length > 0)
          )
        ).map<ExpenseCategory>((name, i) => ({ _id: `local-${i}`, name }));

        const mergedByName = new Map<string, ExpenseCategory>();
        [...fromAPI, ...fromExpenses].forEach((c) => {
          const key = c.name.trim();
          if (!mergedByName.has(key)) mergedByName.set(key, c);
        });
        setExpenseCategories(Array.from(mergedByName.values()));
      } finally {
        if (loadRequestRef.current === requestId) {
          setSupplementalLoading(false);
        }
      }
    },
    [preset, year, range.start, range.end]
  );

  const loadEverything = useCallback(async () => {
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    setLoading(true);
    setSupplementalLoading(false);
    setError(null);
    try {
      const rangeFinance = await getFinanceByRangeAutoWeb(startStr, endStr).catch(
        () =>
          ({
            totalRevenue: 0,
            totalRefunds: 0,
            totalDiscounts: 0,
            totalCost: 0,
            totalNetProfit: 0,
            totalCommissionPaid: 0,
            bestSellers: [],
            events: [],
            businessExpenses: [],
            paymentMethodBreakdown: [],
            paymentMethodBreakdownAll: [],
          } as RangeFinance)
      );

      if (loadRequestRef.current !== requestId) return;

      setData(rangeFinance);
      setEvents(rangeFinance.events || []);
      setExpenses([]);
      setExpenseCategories([]);
      void loadSupplemental(rangeFinance, requestId);
    } catch (e: any) {
      if (loadRequestRef.current !== requestId) return;
      setError(e?.message || "Failed to load finance data");
    } finally {
      if (loadRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [startStr, endStr, loadSupplemental]);

  const ensureInventoryLoaded = useCallback(async () => {
    if (hasLoadedInventory || inventoryLoading) return;
    setInventoryLoading(true);
    try {
      const inventoryRows = await fetchInventoryLite().catch(() => []);
      const inventoryLookup = new Map<string, InventoryLite>();
      const inventoryIdLookup = new Map<string, InventoryLite>();
      (inventoryRows || []).forEach((item) => {
        const keyBySku = makeInventoryKey(item.itemName, item.sku);
        inventoryLookup.set(keyBySku, item);
        const nameKey = makeInventoryKey(item.itemName, "");
        inventoryLookup.set(nameKey, item);
        if (item._id) inventoryIdLookup.set(String(item._id), item);
      });
      setInventoryByKey(inventoryLookup);
      setInventoryById(inventoryIdLookup);
      setHasLoadedInventory(true);
    } finally {
      setInventoryLoading(false);
    }
  }, [hasLoadedInventory, inventoryLoading]);

  useEffect(() => {
    loadEverything();
  }, [loadEverything]);

  useEffect(() => {
    if (!selectedBestSeller) return;
    void ensureInventoryLoaded();
  }, [selectedBestSeller, ensureInventoryLoaded]);

  /* ---------- Cards ---------- */
  const paymentCards = useMemo(() => {
    const list =
      data?.paymentMethodBreakdownAll || data?.paymentMethodBreakdown || [];
    return list.map((p) => ({
      label: p.paymentMethod || "Unknown",
      value: Number(p.totalAmount || 0),
    }));
  }, [data]);

  const summaryCards = useMemo(() => {
    const d = data || ({} as RangeFinance);
    const base = [
      { label: "Total Revenue", value: d.totalRevenue, color: PALETTE.blue },
      {
        label: "Net Profit",
        value: d.totalNetProfit,
        color: Number(d.totalNetProfit || 0) < 0 ? PALETTE.red : PALETTE.green,
      },
      { label: "Total Cost", value: d.totalCost, color: PALETTE.red },
      {
        label: "Employee Payouts",
        value: d.totalCommissionPaid || 0,
        color: PALETTE.violet,
      },
      { label: "Discounts", value: d.totalDiscounts, color: PALETTE.amber },
      { label: "Refunds", value: d.totalRefunds, color: PALETTE.red },
    ];
    return [
      ...base,
      ...paymentCards.map((p) => ({
        label: p.label,
        value: p.value,
        color: PALETTE.slate,
      })),
    ];
  }, [data, paymentCards]);

  /* ---------- Charts data ---------- */
  const pieSlices = useMemo(() => {
    const d = data || ({} as RangeFinance);
    return [
      {
        name: "Revenue",
        value: Math.max(0, Number(d.totalRevenue || 0)),
        color: "url(#grad-green)",
      },
      {
        name: "Net Profit",
        value: Math.max(0, Number(d.totalNetProfit || 0)),
        color: "url(#grad-blue)",
      },
      {
        name: "Refunds",
        value: Math.max(0, Number(d.totalRefunds || 0)),
        color: "url(#grad-red)",
      },
      {
        name: "Discounts",
        value: Math.max(0, Number(d.totalDiscounts || 0)),
        color: "url(#grad-amber)",
      },
    ];
  }, [data]);

  const categoryAnalysis = useMemo(() => {
    const by = new Map<
      string,
      { category: string; totalRevenue: number; totalQuantity: number }
    >();
    (data?.bestSellers || []).forEach((b) => {
      const category = normalizeCategoryLabel(b.category);
      const key = category.toLowerCase();
      const prev = by.get(key) || {
        category,
        totalRevenue: 0,
        totalQuantity: 0,
      };
      prev.totalRevenue += Number(b.totalRevenue || 0);
      prev.totalQuantity += Number(b.quantitySold || 0);
      by.set(key, prev);
    });
    return Array.from(by.values()).sort((a, b) => {
      const revenueDiff = b.totalRevenue - a.totalRevenue;
      if (revenueDiff !== 0) return revenueDiff;
      return b.totalQuantity - a.totalQuantity;
    });
  }, [data?.bestSellers]);
  const categoryAnalysisWithPct = useMemo(() => {
    const totalRevenue = categoryAnalysis.reduce(
      (sum, row) => sum + Number(row.totalRevenue || 0),
      0
    );
    return categoryAnalysis.map((row) => ({
      ...row,
      revenueSharePct:
        totalRevenue > 0 ? (Number(row.totalRevenue || 0) / totalRevenue) * 100 : 0,
    }));
  }, [categoryAnalysis]);
  const visibleCategoryAnalysis = useMemo(
    () =>
      showAllAnalysis
        ? categoryAnalysisWithPct
        : categoryAnalysisWithPct.slice(0, 8),
    [showAllAnalysis, categoryAnalysisWithPct]
  );
  const visibleCategoryAnalysisNumbered = useMemo(
    () =>
      visibleCategoryAnalysis.map((row, index) => ({
        ...row,
        categoryLabel: `${index + 1}. ${row.category}`,
      })),
    [visibleCategoryAnalysis]
  );
  const categoryChartMinWidth = useMemo(
    () => Math.max(540, visibleCategoryAnalysis.length * 128),
    [visibleCategoryAnalysis.length]
  );
  const {
    ref: categoryChartScrollRef,
    isDragging: isCategoryChartDragging,
    dragHandlers: categoryChartDragHandlers,
  } = useDragScroll<HTMLDivElement>();

  /* ---------- Best Sellers: filters + search ---------- */

  // All category labels from best sellers
  const bestSellerCategories = useMemo(() => {
    const set = new Set<string>();
    (data?.bestSellers || []).forEach((b) => {
      const raw = (b.category || "Uncategorized").trim();
      set.add(raw || "Uncategorized");
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data?.bestSellers]);

  const bestSellersFiltered = useMemo(() => {
    const full = data?.bestSellers || [];
    let out = full;

    if (bestCategoryFilter !== "All") {
      const target = bestCategoryFilter.toLowerCase();
      out = out.filter((b) => {
        const cat = (b.category || "Uncategorized").trim() || "Uncategorized";
        return cat.toLowerCase() === target;
      });
    }

    if (bestSearch.trim()) {
      const q = bestSearch.trim().toLowerCase();
      out = out.filter((b) => {
        const name = (b.itemName || "").toLowerCase();
        const sku = (b.sku || "").toLowerCase();
        return name.includes(q) || sku.includes(q);
      });
    }

    return out;
  }, [data?.bestSellers, bestCategoryFilter, bestSearch]);

  const onBestHeaderClick = useCallback((key: BestSortKey) => {
    setShowAllBest(false);
    setBestSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      const defaultDir: BestSortDir =
        key === "itemName" || key === "category" ? "asc" : "desc";
      return { key, dir: defaultDir };
    });
  }, []);

  const visibleBest = useMemo(() => {
    const list = [...bestSellersFiltered].sort((a, b) => {
      const direction = bestSort.dir === "asc" ? 1 : -1;
      let cmp = 0;

      if (bestSort.key === "itemName") {
        cmp = (a.itemName || "").localeCompare(b.itemName || "", undefined, {
          sensitivity: "base",
        });
      } else if (bestSort.key === "category") {
        const aCat = (a.category || "Uncategorized").trim() || "Uncategorized";
        const bCat = (b.category || "Uncategorized").trim() || "Uncategorized";
        cmp = aCat.localeCompare(bCat, undefined, { sensitivity: "base" });
      } else if (bestSort.key === "quantitySold") {
        cmp = Number(a.quantitySold || 0) - Number(b.quantitySold || 0);
      } else {
        cmp = Number(a.totalRevenue || 0) - Number(b.totalRevenue || 0);
      }

      if (cmp !== 0) return cmp * direction;

      const revenueDiff = Number(b.totalRevenue || 0) - Number(a.totalRevenue || 0);
      if (revenueDiff !== 0) return revenueDiff;
      const qtyDiff = Number(b.quantitySold || 0) - Number(a.quantitySold || 0);
      if (qtyDiff !== 0) return qtyDiff;

      return (a.itemName || "").localeCompare(b.itemName || "", undefined, {
        sensitivity: "base",
      });
    });
    return showAllBest ? list : list.slice(0, 5);
  }, [bestSellersFiltered, showAllBest, bestSort]);
  const selectedBestSellerInventory = useMemo(() => {
    if (!selectedBestSeller) return null;
    if (selectedBestSeller.inventoryItemId) {
      const byId = inventoryById.get(String(selectedBestSeller.inventoryItemId));
      if (byId) return byId;
    }
    const bySku = inventoryByKey.get(
      makeInventoryKey(selectedBestSeller.itemName, selectedBestSeller.sku)
    );
    if (bySku) return bySku;
    return inventoryByKey.get(makeInventoryKey(selectedBestSeller.itemName, ""));
  }, [selectedBestSeller, inventoryById, inventoryByKey]);
  const selectedBestSellerSummaryKey = useMemo(() => {
    if (!selectedBestSeller) return "";
    return selectedBestSeller.inventoryItemId
      ? `inventory:${selectedBestSeller.inventoryItemId}`
      : makeInventoryKey(
          selectedBestSeller.itemName,
          selectedBestSellerInventory?._id || selectedBestSeller.sku || ""
        );
  }, [selectedBestSeller, selectedBestSellerInventory]);

  useEffect(() => {
    let active = true;

    if (!selectedBestSeller) {
      setBestSellerInventorySummary({ poolQuantity: 0, assignedQuantity: 0 });
      setBestSellerInventorySummaryLoading(false);
      return;
    }

    if (inventoryLoading || !hasLoadedInventory) {
      setBestSellerInventorySummaryLoading(true);
      return () => {
        active = false;
      };
    }

    const cached = bestSellerInventorySummaryCache.get(selectedBestSellerSummaryKey);
    if (cached) {
      setBestSellerInventorySummary(cached);
      setBestSellerInventorySummaryLoading(false);
      return () => {
        active = false;
      };
    }

    const loadBestSellerInventorySummary = async () => {
      setBestSellerInventorySummaryLoading(true);
      try {
        const inventoryId = selectedBestSellerInventory?._id;
        const [pools, eventInventoryRows] = await Promise.all([
          fetchInventoryPools().catch(() => []),
          Promise.all(
            (events || [])
              .filter((event: any) => isOpenEventStatus(event?.status))
              .map((event) => getEventId(event))
              .filter(Boolean)
              .map((eventId) => getEventInventory(eventId).catch(() => []))
          ).catch(() => []),
        ]);

        const poolQuantity = (
          await Promise.all(
            (pools || [])
              .filter((pool) => !pool.isDefault && String(pool._id) !== "main")
              .map(async (pool) => {
                const items = await getInventoryByPool(String(pool._id)).catch(
                  () => []
                );
                return (items || [])
                  .filter((item) =>
                    matchesInventoryRecord(item, selectedBestSeller, inventoryId)
                  )
                  .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
              })
          )
        ).reduce((sum, qty) => sum + Number(qty || 0), 0);

        const assignedQuantity = (eventInventoryRows || [])
          .flat()
          .filter((row) =>
            matchesInventoryRecord(row, selectedBestSeller, inventoryId)
          )
          .reduce((sum, row) => sum + readAssignedQuantity(row), 0);

        if (!active) return;
        const summary = {
          poolQuantity,
          assignedQuantity,
        };
        setBestSellerInventorySummary(summary);
        setBestSellerInventorySummaryCache((prev) => {
          const next = new Map(prev);
          next.set(selectedBestSellerSummaryKey, summary);
          return next;
        });
      } finally {
        if (active) setBestSellerInventorySummaryLoading(false);
      }
    };

    void loadBestSellerInventorySummary();

    return () => {
      active = false;
    };
  }, [
    events,
    hasLoadedInventory,
    inventoryLoading,
    bestSellerInventorySummaryCache,
    selectedBestSeller,
    selectedBestSellerInventory,
    selectedBestSellerSummaryKey,
  ]);


  // NEW: expense category filtering
  const rangeFilteredExpenses = useMemo(() => {
    const rangeStart = startOfDay(range.start).getTime();
    const rangeEnd = endOfDay(range.end).getTime();
    return (expenses || []).filter((expense) => {
      const expenseDate = getExpenseDate(expense);
      if (!expenseDate) return false;
      const time = expenseDate.getTime();
      return time >= rangeStart && time <= rangeEnd;
    });
  }, [expenses, range.start, range.end]);

  const filteredExpenses = useMemo(() => {
    let next = rangeFilteredExpenses;

    if (selectedExpenseCategory !== "All") {
      const sel = selectedExpenseCategory.trim().toLowerCase();
      next = next.filter(
        (e) => (e.category || "").trim().toLowerCase() === sel
      );
    }

    return [...next].sort((a, b) => {
      const aTime = getExpenseDate(a)?.getTime() ?? 0;
      const bTime = getExpenseDate(b)?.getTime() ?? 0;
      if (bTime !== aTime) return bTime - aTime;
      return (b._id || "").localeCompare(a._id || "");
    });
  }, [rangeFilteredExpenses, selectedExpenseCategory]);

  // visible expenses (top 5, toggleable) AFTER filter
  const visibleExpenses = useMemo(() => {
    const list = filteredExpenses || [];
    return showAllExpenses ? list : list.slice(0, 5);
  }, [filteredExpenses, showAllExpenses]);

  /* ---------- Handlers ---------- */
  const onPresetClick = (k: PresetKey) => {
    setPreset(k);
    setRange(makePresetRange(k, allTimeStart));
  };
  const onApplyCustom = (r: DateRange) => {
    setPreset("custom");
    setRange(r);
  };

  // Totals for *all* expenses by category (sorted by spend desc) + grand total
  const expenseTotalsAll = useMemo(() => {
    const map = new Map<string, { total: number; count: number }>();
    (rangeFilteredExpenses || []).forEach((e) => {
      const key = (e.category || "Uncategorized").trim() || "Uncategorized";
      const agg = map.get(key) || { total: 0, count: 0 };
      agg.total += Number(e.cost || 0);
      agg.count += 1;
      map.set(key, agg);
    });
    const rows = Array.from(map, ([category, v]) => ({ category, ...v })).sort(
      (a, b) => b.total - a.total
    );
    const grand = rows.reduce(
      (acc, r) => ({ total: acc.total + r.total, count: acc.count + r.count }),
      { total: 0, count: 0 }
    );
    return { rows, grand };
  }, [rangeFilteredExpenses]);

  // Totals *after current filter* (so the numbers match what you see)
  const expenseTotalsFiltered = useMemo(() => {
    const map = new Map<string, { total: number; count: number }>();
    (filteredExpenses || []).forEach((e) => {
      const key = (e.category || "Uncategorized").trim() || "Uncategorized";
      const agg = map.get(key) || { total: 0, count: 0 };
      agg.total += Number(e.cost || 0);
      agg.count += 1;
      map.set(key, agg);
    });
    const rows = Array.from(map, ([category, v]) => ({ category, ...v })).sort(
      (a, b) => b.total - a.total
    );
    const grand = rows.reduce(
      (acc, r) => ({ total: acc.total + r.total, count: acc.count + r.count }),
      { total: 0, count: 0 }
    );
    return { rows, grand };
  }, [filteredExpenses]);

  const isInitialLoading = loading && !data;

  /* -------------------- Render -------------------- */
  return (
    <div className="pt-10 md:pt-12 p-0 md:p-0">
      <ScreenLockerWeb
        storageKey="finance-dashboard"
        autoLockMs={5 * 60 * 1000}
      >
        {(lockControl) => (
        <div className="px-4 md:px-6 py-4 ">
          <div className="mx-auto max-w-6xl space-y-4">
            {/* Header & compact inline controls */}
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-semibold leading-tight">
                    Finance Overview
                  </h1>
                  {lockControl}
                </div>
                <p className="text-xs text-slate-500">{fmtRangeLabel(range)}</p>
              </div>
              <div className="flex flex-wrap items-start gap-2">
                <button
                  onClick={() => navigate("/finance/tax-reports")}
                  className="px-3 py-1.5 rounded-md border text-xs border-blue-300 text-blue-700 hover:bg-blue-50 self-start bg-white"
                >
                  Sales Tax Reports
                </button>
              </div>
              <div className="flex items-start gap-2 ">
                <DateRangeControls
                  preset={preset}
                  range={range}
                  onPreset={onPresetClick}
                  onApply={onApplyCustom}
                />
                <button
                  onClick={loadEverything}
                  className="px-3 py-1.5 rounded-md border text-xs border-slate-300 hover:bg-slate-50 self-start bg-white"
                >
                  Refresh
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm">
                {error}
              </div>
            )}

            {isInitialLoading ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <FinanceSkeletonBlock key={i} className="h-[78px] p-3" />
                  ))}
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                  <FinanceSkeletonBlock className="h-[290px] p-3" />
                  <FinanceSkeletonBlock className="h-[290px] p-3" />
                </div>
                <FinanceSkeletonBlock className="h-[360px] p-3" />
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                  <FinanceSkeletonBlock className="h-[320px] p-3" />
                  <FinanceSkeletonBlock className="h-[320px] p-3" />
                </div>
              </>
            ) : (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 ">
                  {(showAllCards ? summaryCards : summaryCards.slice(0, 6)).map(
                    (c, i) => (
                      <div key={i} className="rounded-md border p-3 bg-white">
                        <div className="text-[11px] text-slate-500 leading-tight">
                          {c.label}
                        </div>
                        <div
                          className="text-base font-semibold tabular-nums"
                          style={{ color: c.color }}
                        >
                          {summaryCurrency(c.label, c.value || 0)}
                        </div>
                      </div>
                    )
                  )}
                </div>
                {summaryCards.length > 6 && (
                  <div className="flex justify-center">
                    <button
                      onClick={() => setShowAllCards((v) => !v)}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {showAllCards ? "Show less" : "Show more"}
                    </button>
                  </div>
                )}

                {/* Charts */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 ">
                  <div className="rounded-md border p-3 bg-white">
                    <div className="mb-1 font-medium text-sm">Percentage Pie</div>
                    <div className="h-56">
                      <ResponsiveContainer>
                        <RPieChart>
                      <defs>
                        <linearGradient
                          id="grad-green"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#22c55e"
                            stopOpacity={0.95}
                          />
                          <stop
                            offset="100%"
                            stopColor="#16a34a"
                            stopOpacity={0.85}
                          />
                        </linearGradient>
                        <linearGradient
                          id="grad-blue"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#60a5fa"
                            stopOpacity={0.95}
                          />
                          <stop
                            offset="100%"
                            stopColor="#2563eb"
                            stopOpacity={0.85}
                          />
                        </linearGradient>
                        <linearGradient
                          id="grad-red"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#f87171"
                            stopOpacity={0.95}
                          />
                          <stop
                            offset="100%"
                            stopColor="#dc2626"
                            stopOpacity={0.85}
                          />
                        </linearGradient>
                        <linearGradient
                          id="grad-amber"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#fbbf24"
                            stopOpacity={0.95}
                          />
                          <stop
                            offset="100%"
                            stopColor="#d97706"
                            stopOpacity={0.85}
                          />
                        </linearGradient>
                      </defs>

                      <Pie
                        dataKey="value"
                        data={pieSlices}
                        nameKey="name"
                        innerRadius={54}
                        outerRadius={92}
                      >
                        {pieSlices.map((s, idx) => (
                          <Cell key={idx} fill={s.color as string} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: any) => currency(Number(v))} />
                      <Legend />
                        </RPieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="rounded-md border p-3 bg-white">
                    <div className="mb-1 font-medium text-sm flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span>Category Analysis</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                          {categoryAnalysis.length}
                        </span>
                      </div>
                      {categoryAnalysis.length > 8 && (
                        <button
                          className="text-sm text-blue-600"
                          onClick={() => setShowAllAnalysis((v) => !v)}
                        >
                          {showAllAnalysis ? "Show less" : "Show more"}
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2 py-1 text-violet-700">
                        <span className="h-2 w-2 rounded-full bg-violet-600" />
                        Qty
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-1 text-blue-700">
                        <span className="h-2 w-2 rounded-full bg-blue-600" />
                        Revenue %
                      </span>
                    </div>
                    <span className="text-slate-500">
                      Click and drag to scroll
                    </span>
                  </div>
                  <div
                    ref={categoryChartScrollRef}
                    {...categoryChartDragHandlers}
                    className={`h-72 overflow-x-auto overflow-y-hidden rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50/70 pl-1 pr-1 pt-3 pb-1 select-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [&_.recharts-wrapper]:cursor-inherit [&_.recharts-surface]:cursor-inherit [&_.recharts-layer]:cursor-inherit [&_.recharts-cartesian-axis-tick-value]:cursor-inherit ${
                      isCategoryChartDragging ? "cursor-grabbing" : "cursor-grab"
                    }`}
                  >
                    <div
                      className="h-full"
                      style={{ minWidth: `${categoryChartMinWidth}px` }}
                    >
                      <ResponsiveContainer>
                        <RBarChart
                          data={visibleCategoryAnalysisNumbered}
                          barGap={12}
                          barCategoryGap="34%"
                          margin={{ top: 34, right: 14, left: -8, bottom: 26 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="categoryLabel"
                            interval={0}
                            height={46}
                            angle={-14}
                            textAnchor="end"
                            tickMargin={8}
                            tickFormatter={(t) => String(t)}
                          />
                          <YAxis
                            yAxisId="qty"
                            allowDecimals={false}
                            tickFormatter={(v: any) => formatCount(Number(v))}
                          />
                          <YAxis
                            yAxisId="revenue"
                            orientation="right"
                            domain={[0, 100]}
                            tickFormatter={(v: any) => formatPercent(Number(v))}
                          />
                          <Tooltip
                            formatter={(v: any, name: any) =>
                              formatCategoryMetric(Number(v), String(name))
                            }
                          />
                          <Bar
                            yAxisId="qty"
                            dataKey="totalQuantity"
                            name="Qty"
                            fill="#7c3aed"
                            radius={[4, 4, 0, 0]}
                            barSize={18}
                          >
                            <LabelList
                              dataKey="totalQuantity"
                              position="top"
                              formatter={(v: any) => formatCount(Number(v))}
                              style={{
                                fill: "#5b21b6",
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            />
                          </Bar>
                          <Bar
                            yAxisId="revenue"
                            dataKey="revenueSharePct"
                            name="Revenue %"
                            fill="#2563eb"
                            radius={[4, 4, 0, 0]}
                            barSize={18}
                          >
                            <LabelList
                              dataKey="revenueSharePct"
                              position="top"
                              formatter={(v: any) => formatPercent(Number(v))}
                              style={{
                                fill: "#1d4ed8",
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            />
                          </Bar>
                        </RBarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                    </div>
                  </div>
                </div>

                {/* Best Sellers (filters + search + table) */}
                <div className="rounded-md border bg-white">
              <div className="px-3 pt-2 pb-2 border-b space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-sm">Best Sellers</div>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                      {bestSellersFiltered.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="search"
                      value={bestSearch}
                      onChange={(e) => {
                        setBestSearch(e.target.value);
                        setShowAllBest(false); // reset pagination on new search
                      }}
                      placeholder="Search item or SKU"
                      className="border border-slate-300 rounded-md px-2 py-1 text-xs w-40 md:w-56"
                    />
                  </div>
                </div>

                {/* Category filter chips */}
                <div className="flex flex-wrap gap-2">
                  <button
                    className={`px-2.5 py-1 rounded-full text-xs border ${
                      bestCategoryFilter === "All"
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                    onClick={() => {
                      setBestCategoryFilter("All");
                      setShowAllBest(false);
                    }}
                  >
                    All
                  </button>
                  {bestSellerCategories.map((cat) => {
                    const active = bestCategoryFilter === cat;
                    return (
                      <button
                        key={cat}
                        onClick={() => {
                          setBestCategoryFilter(active ? "All" : cat);
                          setShowAllBest(false);
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs border ${
                          active
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        }`}
                        title={cat}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>

              {bestSellersFiltered.length === 0 ? (
                <div className="px-3 py-3 text-sm text-slate-500">
                  {(data?.bestSellers?.length || 0) === 0
                    ? "No best sellers available."
                    : "No best sellers match your filters."}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="w-12 px-3 py-2 text-right font-medium">
                            #
                          </th>
                          <th className="px-3 py-2 text-left font-medium">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 hover:text-slate-900"
                              onClick={() => onBestHeaderClick("itemName")}
                              title="Sort by item"
                            >
                              <span className="text-[15px] font-black leading-none text-slate-800">
                                {bestSort.key === "itemName"
                                  ? bestSort.dir === "asc"
                                    ? "↑"
                                    : "↓"
                                  : "↕"}
                              </span>
                              Item
                            </button>
                          </th>
                          <th className="px-3 py-2 text-left font-medium">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 hover:text-slate-900"
                              onClick={() => onBestHeaderClick("category")}
                              title="Sort by category"
                            >
                              <span className="text-[15px] font-black leading-none text-slate-800">
                                {bestSort.key === "category"
                                  ? bestSort.dir === "asc"
                                    ? "↑"
                                    : "↓"
                                  : "↕"}
                              </span>
                              Category
                            </button>
                          </th>
                          <th className="px-3 py-2 text-right font-medium">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 hover:text-slate-900"
                              onClick={() => onBestHeaderClick("quantitySold")}
                              title="Sort by quantity"
                            >
                              <span className="text-[15px] font-black leading-none text-slate-800">
                                {bestSort.key === "quantitySold"
                                  ? bestSort.dir === "asc"
                                    ? "↑"
                                    : "↓"
                                  : "↕"}
                              </span>
                              Qty
                            </button>
                          </th>
                          <th className="px-3 py-2 text-right font-medium">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 hover:text-slate-900"
                              onClick={() => onBestHeaderClick("totalRevenue")}
                              title="Sort by revenue"
                            >
                              <span className="text-[15px] font-black leading-none text-slate-800">
                                {bestSort.key === "totalRevenue"
                                  ? bestSort.dir === "asc"
                                    ? "↑"
                                    : "↓"
                                  : "↕"}
                              </span>
                              Revenue
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleBest.map((b, i) => (
                          <tr
                            key={`${b.sku || b.itemName}-${i}`}
                            className="cursor-pointer even:bg-slate-50/40 hover:bg-blue-50/50"
                            onClick={() => setSelectedBestSeller(b)}
                            title="View item details"
                          >
                            <td className="px-3 py-2 text-right text-slate-500 tabular-nums">
                              {i + 1}
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-left text-blue-700">
                                {truncate(b.itemName)}
                              </div>
                              {b.sku && (
                                <div className="text-[11px] text-slate-500">
                                  SKU: {b.sku}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-slate-600">
                              {b.category || "Uncategorized"}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {b.quantitySold}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {currency(b.totalRevenue || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {bestSellersFiltered.length > 5 && (
                    <div className="px-3 py-2 border-t flex justify-end">
                      <button
                        className="text-sm text-blue-600"
                        onClick={() => setShowAllBest((v) => !v)}
                      >
                        {showAllBest ||
                        visibleBest.length >= bestSellersFiltered.length
                          ? "Show less"
                          : "Show more"}
                      </button>
                    </div>
                  )}
                </>
              )}
                </div>

                {/* Business Expenses (filters + table) */}
                <div className="rounded-md border bg-white">
              {/* Header + Action buttons */}
              <div className="px-3 pt-2 pb-1 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-sm">Business Expenses</div>
                    {supplementalLoading && (
                      <span className="text-[11px] text-slate-500">
                        Loading details…
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowCategories(true)}
                      className="text-sm text-slate-700 border border-slate-300 rounded-md px-2.5 py-1 hover:bg-slate-50"
                      title="Manage categories"
                    >
                      Categories
                    </button>
                    <button
                      onClick={() => setShowAddExpense(true)}
                      className="text-sm text-blue-600"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Totals bar (matches current filter) */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {/* Quick category totals chips (click to filter) */}
                  <div className="flex flex-wrap gap-2">
                    {expenseTotalsAll.rows.map((r) => {
                      const active = selectedExpenseCategory === r.category;
                      return (
                        <button
                          key={`tot-${r.category}`}
                          onClick={() =>
                            setSelectedExpenseCategory(
                              active ? "All" : (r.category as string)
                            )
                          }
                          className={`px-2.5 py-1 rounded-full text-xs border ${
                            active
                              ? "bg-blue-600 text-white border-blue-600"
                              : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                          }`}
                          title={`${r.count} • ${currency(r.total)}`}
                        >
                          <span className="font-medium">{r.category}</span>{" "}
                          <span className="opacity-80">
                            {r.count} • {currency(r.total)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="px-2.5 py-1 rounded-md border text-xs bg-white">
                    <span className="text-slate-600 mr-1">Total</span>
                    <span className="font-medium">
                      {expenseTotalsFiltered.grand.count}
                    </span>
                    <span className="text-slate-500 mx-1">items</span>
                    <span className="font-semibold">
                      {currency(expenseTotalsFiltered.grand.total)}
                    </span>
                  </div>
                </div>
              </div>

              {supplementalLoading && filteredExpenses.length === 0 ? (
                <div className="px-3 py-3 text-sm text-slate-500">
                  Loading expenses…
                </div>
              ) : filteredExpenses.length === 0 ? (
                <div className="px-3 py-3 text-sm text-slate-500">
                  {selectedExpenseCategory === "All"
                    ? "No expense added yet."
                    : `No expenses in “${selectedExpenseCategory}”.`}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">
                          Description
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          Category
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          Date
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          Amount
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleExpenses.map((e) => (
                        <tr key={e._id} className="even:bg-slate-50/40">
                          <td className="px-3 py-2">
                            {truncate(e.description, 64)}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {e.category || "—"}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {new Date(
                              getExpenseDateValue(e)
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </td>
                          <td className="px-3 py-2 text-right text-red-600 tabular-nums">
                            {currency(e.cost || 0)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex items-center justify-end gap-3">
                              <button
                                onClick={() => {
                                  setEditingExpense(e);
                                  setShowEditExpense(true);
                                }}
                                className="text-xs text-slate-700 hover:underline"
                              >
                                Edit
                              </button>
                              <button
                                onClick={async () => {
                                  const ok =
                                    typeof window !== "undefined" &&
                                    window.confirm("Delete this expense?");
                                  if (!ok) return;
                                  await deleteBusinessExpense(year, e._id);
                                  await loadEverything();
                                }}
                                className="text-xs text-red-600 hover:underline"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Bottom toggle if long list */}
                  {filteredExpenses.length > 5 && (
                    <div className="flex justify-center py-2">
                      <button
                        onClick={() => setShowAllExpenses((v) => !v)}
                        className="text-sm text-blue-600"
                      >
                        {showAllExpenses ? "Show less" : "Show more"}
                      </button>
                    </div>
                  )}
                </div>
              )}
                </div>

                {/* Events (table) */}
                {(events.length > 0 || supplementalLoading) && (
                  <div className="rounded-md border bg-white">
                <div className="px-3 py-2 border-b">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-sm">Events</div>
                      {supplementalLoading && (
                        <span className="text-[11px] text-slate-500">
                          Loading details…
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="search"
                        value={eventSearch}
                        onChange={(e) => setEventSearch(e.target.value)}
                        placeholder="Search events"
                        className="h-8 w-44 rounded-md border border-slate-200 px-2 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        aria-label="Search events by name"
                      />
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">
                          Event
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          Date
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          Revenue
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          Net
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          Best Seller
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          Open
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleEvents.map((ev) => {
                        const eid = getEventId(ev);
                        const disabled = !eid;
                        return (
                          <tr
                            key={eid || ev.title}
                            className={`even:bg-slate-50/40 ${
                              disabled
                                ? ""
                                : "cursor-pointer transition-colors hover:bg-blue-50 focus-visible:bg-blue-50 focus-visible:outline-none"
                            }`}
                            role={disabled ? undefined : "link"}
                            tabIndex={disabled ? undefined : 0}
                            title={disabled ? undefined : "Open Financial Report"}
                            onClick={() => {
                              if (!disabled) {
                                navigate(
                                  `/finance/event/${encodeURIComponent(eid)}`,
                                );
                              }
                            }}
                            onKeyDown={(event) => {
                              if (
                                !disabled &&
                                (event.key === "Enter" || event.key === " ")
                              ) {
                                event.preventDefault();
                                navigate(
                                  `/finance/event/${encodeURIComponent(eid)}`,
                                );
                              }
                            }}
                          >
                            <td className="px-3 py-2">
                              {truncate(ev.title, 60)}
                            </td>
                            <td className="px-3 py-2 text-slate-600">
                              <div className="flex flex-col">
                                <span>
                                  {formatEventDate(
                                    ev.startDateTime || ev.eventStartDateTime
                                  )}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {formatEventDate(
                                    ev.endDateTime ||
                                      ev.eventEndDateTime ||
                                      ev.endDate ||
                                      ev.startDateTime ||
                                      ev.eventStartDateTime
                                  )}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {currency(ev.totalRevenue || 0)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {currency(ev.totalNetProfit || 0)}
                            </td>
                            <td className="px-3 py-2 text-slate-600">
                              {ev.bestSellerItem
                                ? `${truncate(
                                    ev.bestSellerItem.itemName,
                                    28
                                  )} (${ev.bestSellerItem.quantitySold})`
                                : "—"}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {disabled ? (
                                <span className="text-xs text-slate-400">
                                  N/A
                                </span>
                              ) : (
                                <span className="text-xs text-blue-600">
                                  View →
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {supplementalLoading && visibleEvents.length === 0 && (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-3 py-4 text-center text-sm text-slate-500"
                          >
                            Loading events…
                          </td>
                        </tr>
                      )}
                      {!supplementalLoading && visibleEvents.length === 0 && (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-3 py-4 text-center text-sm text-slate-500"
                          >
                            No events match "{eventSearch.trim()}".
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {filteredEvents.length > 6 && (
                  <div className="flex justify-center py-2">
                    <button
                      className="text-sm text-blue-600"
                      onClick={() => setShowAllEvents((v) => !v)}
                    >
                      {showAllEvents ? "Show less" : "Show more"}
                    </button>
                  </div>
                )}
                  </div>
                )}
              </>
            )}

            {selectedBestSeller && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                onClick={() => setSelectedBestSeller(null)}
              >
                <div
                  className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-4 shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-semibold text-slate-900">
                        {selectedBestSeller.itemName}
                      </div>
                      <div className="text-xs text-slate-500">
                        {selectedBestSeller.sku
                          ? `SKU: ${selectedBestSeller.sku}`
                          : "No SKU"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedBestSeller(null)}
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                    >
                      Close
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="h-80 w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-900/90">
                      {selectedBestSellerInventory?.imageUrl ||
                      selectedBestSeller.imageUrl ? (
                        <img
                          src={
                            selectedBestSellerInventory?.imageUrl ||
                            selectedBestSeller.imageUrl
                          }
                          alt={selectedBestSeller.itemName}
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-xs text-slate-500">
                          No Image
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">
                          Quantity Left (Main Inventory)
                        </div>
                        <div className="text-lg font-semibold tabular-nums text-slate-900">
                          {inventoryLoading && !selectedBestSellerInventory
                            ? "Loading…"
                            : selectedBestSellerInventory?.quantity ?? "—"}
                        </div>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">
                          Quantity Left (Other Pools)
                        </div>
                        <div className="text-lg font-semibold tabular-nums text-slate-900">
                          {bestSellerInventorySummaryLoading
                            ? "Loading..."
                            : bestSellerInventorySummary.poolQuantity}
                        </div>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">
                          Assigned to Events
                        </div>
                        <div className="text-lg font-semibold tabular-nums text-slate-900">
                          {bestSellerInventorySummaryLoading
                            ? "Loading..."
                            : bestSellerInventorySummary.assignedQuantity}
                        </div>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">Quantity Sold</div>
                        <div className="text-lg font-semibold tabular-nums text-slate-900">
                          {selectedBestSeller.quantitySold}
                        </div>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">Category</div>
                        <div className="font-medium text-slate-900">
                          {selectedBestSeller.category || "Uncategorized"}
                        </div>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">Revenue</div>
                        <div className="font-medium tabular-nums text-slate-900">
                          {currency(selectedBestSeller.totalRevenue || 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loading && !isInitialLoading && (
              <div className="fixed inset-0 bg-black/20 flex items-center justify-center">
                <div className="bg-white rounded-md p-4 border shadow text-sm">
                  Loading…
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </ScreenLockerWeb>

      {/* Categories modal mounted once at page root */}
      <CategoriesModal
        open={showCategories}
        onClose={() => setShowCategories(false)}
      />
      <EditExpenseModal
        open={showEditExpense}
        onClose={() => setShowEditExpense(false)}
        expense={editingExpense}
        year={year}
        onSaved={async () => {
          await loadEverything();
          // keep current filter selection stable
          if (editingExpense?.category && selectedExpenseCategory !== "All") {
            setSelectedExpenseCategory(editingExpense.category);
          }
        }}
      />

      {/* Add Expense (now with category) */}
      <AddExpenseModal
        open={showAddExpense}
        onClose={() => setShowAddExpense(false)}
        onSubmit={async ({ description, cost, date, category }) => {
          await addBusinessExpense({ description, cost, date, category });
          await loadEverything();
          // keep current category selection if user just added in that category
          if (category && selectedExpenseCategory !== "All") {
            setSelectedExpenseCategory(category);
          }
        }}
      />
    </div>
  );
};

export default FinanceDashboard;
